import {useMutation, useQuery} from '@apollo/client';
import {Empty, Pagination, Spin, Table} from 'antd';
import {useState} from 'react';
import {useSelector} from 'react-redux';
import {toast} from 'react-toastify';

import {
  CREATE_STOCK_REWARD_BANK,
  UPDATE_STOCK_REWARD_BANK,
} from 'graphql/mutations/rewardScheme';
import {
  GET_ALL_STOCK_REWARDS,
  GET_PUBLIC_EQUITY_INSTRUMENTS,
} from 'graphql/queries/rewardScheme';
import {makeSelectTabOperations} from 'redux/store/auth';
import {NavTabsEnum} from 'utils/constants';
import {walletCurrencies} from 'utils/mock';
import RewardBankModal from './RewardBankModal';
import {Remaining, readMutation, statOf} from './rewardBankShared';
import styles from './rewardBanks.module.scss';

const StockRewardBank = () => {
  const [modal, setModal] = useState({
    open: false,
    mode: 'CREATE',
    record: null,
  });
  const [currentPage, setCurrentPage] = useState(1);
  const [limit, setLimit] = useState(10);

  const tabOperations = useSelector(state =>
    makeSelectTabOperations(state, NavTabsEnum.RewardBanks),
  );

  const {loading, refetch, data} = useQuery(GET_ALL_STOCK_REWARDS, {
    variables: {page: 1, limit: 10},
    errorPolicy: 'all',
    notifyOnNetworkStatusChange: true,
    onError: error => toast.error(error?.message),
  });

  // The old query asked for one hardcoded exchange's company stocks and, since
  // instrument v2, returned nothing — the ticker dropdown was simply empty, so
  // no reward bank could be created at all.
  const {loading: instrumentsLoading, data: instrumentsData} = useQuery(
    GET_PUBLIC_EQUITY_INSTRUMENTS,
    {
      variables: {
        input: {
          explore: false,
          // `assetClassKey` is `AssetClassKey!` — public equities live under
          // EQUITIES, and omitting it fails the whole query.
          assetClassKey: 'EQUITIES',
          instrumentKey: 'PUBLIC_EQUITY',
          paginate: true,
          page: 1,
          limit: 200,
        },
      },
      errorPolicy: 'all',
    },
  );

  const [createStockRewardBank, {loading: creating}] = useMutation(
    CREATE_STOCK_REWARD_BANK,
  );
  const [updateStockRewardBank, {loading: updating}] = useMutation(
    UPDATE_STOCK_REWARD_BANK,
  );

  const result = data?.getAllStockBanks;
  const banks = result?.data?.stockRewardBank ?? [];
  const stats = result?.data?.stockStats;
  const total = result?.pagination?.total ?? banks.length;

  const instrumentOptions = (
    instrumentsData?.getFinancialInstruments?.data ?? []
  )
    .filter(item => item?.id)
    .map(item => ({
      value: item.id,
      label: `${item?.ticker ?? item?.symbol ?? ''} — ${
        item?.name ?? ''
      }`.trim(),
    }));

  const closeModal = () => setModal(current => ({...current, open: false}));

  const handleSubmit = async values => {
    const isEdit = modal.mode === 'EDIT';
    const outcome = await readMutation(
      isEdit
        ? updateStockRewardBank({
            variables: {
              data: {
                stockBankId: modal.record?.id,
                price: Number(values.price || 0),
                purchasedUnits: values.amount,
              },
            },
          })
        : createStockRewardBank({
            variables: {
              // `ticker`, `companyId` and `stockExchangeId` are rejected with a
              // migration error. The instrument is the identity now, and the
              // server stamps ticker and exchange from it.
              data: {
                instrumentId: values.instrumentId,
                currency: values.currency,
                price: Number(values.price || 0),
                purchasedUnits: values.amount,
              },
            },
          }),
      isEdit ? 'updateStockRewardBank' : 'createStockRewardBank',
    );

    if (!outcome.ok) return toast.error(outcome.message);
    toast.success(isEdit ? 'Stock account updated' : 'Stock account created');
    closeModal();
    refetch();
  };

  const columns = [
    {
      title: 'Instrument',
      key: 'ticker',
      render: (_, record) => (
        <span className={styles.ticker}>
          <span className={styles.tickerSymbol}>{record?.ticker ?? '—'}</span>
          {record?.exchangeCode && (
            <span className={styles.tickerName}>{record.exchangeCode}</span>
          )}
        </span>
      ),
    },
    {
      title: 'Purchased',
      key: 'purchasedUnits',
      render: (_, record) => (
        <span className={styles.money}>
          {Number(record?.purchasedUnits ?? 0).toLocaleString('en-US')}
        </span>
      ),
    },
    {
      title: 'Disbursed',
      key: 'disbursedUnits',
      render: (_, record) => (
        <span className={styles.moneyMuted}>
          {Number(record?.disbursedUnits ?? 0).toLocaleString('en-US')}
        </span>
      ),
    },
    {
      title: 'Left',
      key: 'unitsLeft',
      render: (_, record) => (
        <Remaining
          used={record?.disbursedUnits}
          total={record?.purchasedUnits}
          left={record?.unitsLeft}
        />
      ),
    },
    {
      title: 'Actions',
      key: 'actions',
      render: (_, record) => (
        <button
          type="button"
          className={styles.rowAction}
          disabled={!tabOperations?.update}
          onClick={() =>
            setModal({
              open: true,
              mode: 'EDIT',
              record: {...record, label: `${record?.ticker} units`},
            })
          }>
          Top up
        </button>
      ),
    },
  ];

  return (
    <div className={styles.page}>
      <div className={styles.header}>
        <div>
          <h1 className={styles.title}>Stock reward bank</h1>
          <p className={styles.subtitle}>
            Shares held back to hand out as rewards. Each holding is drawn down
            as units are given away.
          </p>
        </div>
        <button
          type="button"
          className={styles.primaryButton}
          disabled={!tabOperations?.create}
          onClick={() => setModal({open: true, mode: 'CREATE', record: null})}>
          Create stock account
        </button>
      </div>

      {/* Straight from `stockStats`. The holdings count is deliberately not
          here: it would be a row count this screen worked out, not a figure
          the service reported. */}
      <div className={styles.stats}>
        {statOf('Units given away', stats?.totalStocksUnitIssued ?? 0)}
        {statOf('Units left', stats?.totalStocksUnitsLeft ?? 0)}
        {statOf(
          'New users from stock rewards',
          stats?.totalNewUsersFromStockReward ?? 0,
        )}
      </div>

      <div className={styles.tableContainer}>
        {loading && !banks.length ? (
          <div className={styles.loading}>
            <Spin size="large" />
            <span>Loading stock accounts…</span>
          </div>
        ) : (
          <>
            <Table
              rowKey={record => record?.id}
              columns={columns}
              dataSource={banks}
              loading={loading}
              pagination={false}
              scroll={{x: 'max-content'}}
              locale={{
                emptyText: (
                  <Empty
                    image={Empty.PRESENTED_IMAGE_SIMPLE}
                    description="No stock accounts yet. Create one to start funding stock rewards."
                  />
                ),
              }}
            />
            {total > limit && (
              <Pagination
                className="ant-table-pagination"
                current={currentPage}
                pageSize={limit}
                total={total}
                showSizeChanger
                showQuickJumper
                onChange={(page, size) => {
                  const nextLimit = size ?? limit;
                  const nextPage = nextLimit !== limit ? 1 : page;
                  setLimit(nextLimit);
                  setCurrentPage(nextPage);
                  refetch({page: nextPage, limit: nextLimit});
                }}
              />
            )}
          </>
        )}
      </div>

      <RewardBankModal
        isOpen={modal.open}
        mode={modal.mode}
        kind="STOCK"
        record={modal.record}
        options={instrumentOptions}
        optionsLoading={instrumentsLoading}
        currencyOptions={walletCurrencies.map(({key, name}) => ({
          value: key,
          label: `${key} — ${name}`,
        }))}
        saving={creating || updating}
        onClose={closeModal}
        onSubmit={handleSubmit}
      />
    </div>
  );
};

export {StockRewardBank};
